import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { finalize } from 'rxjs';

import {
  CarrierResponse,
  LoadingListItemsResponse,
  LoadingListManagementRequest,
  LoadingListService,
  TourGroupsResponse
} from '../../../services/loading-list.service';

import { Warehouse } from '../../../interfaces/Warehouses.interfaces';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

export interface LoadingListInvalidateDialogData {
  warehouses: Warehouse[];
}

type LoadingListsAction = 'invalidate' | 'validate';

type UiParcelItem = LoadingListItemsResponse & {
  selected: boolean;
  weightKgNum: number;
  volumeM3Num: number;
  originalIndex: number;
};

@Component({
  selector: 'app-loading-list-invalidate-dialog',
  standalone: true,
  templateUrl: './loading-list-invalidate-dialog.component.html',
  styleUrls: ['./loading-list-invalidate-dialog.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule
  ]
})
export class LoadingListInvalidateDialogComponent implements OnInit {
  form!: FormGroup;

  step: 1 | 2 = 1;
  selectedTab: LoadingListsAction = 'invalidate';

  tourGroups: TourGroupsResponse[] = [];
  carriers: CarrierResponse[] = [];

  loadingTours = false;
  loadingCarriers = false;
  loadingItems = false;
  loadingItemsSpinner = false;
  submitting = false;

  private barcodeFilterTimer: any = null;

  isExtraVisible = false;

  validItems: UiParcelItem[] = [];
  visibleItems: UiParcelItem[] = [];
  filteredItemsCount = 0;

  barcodeFilter = '';
  onlyUrgent = false;

  selectedMap: Map<string, boolean> = new Map();

  constructor(
    private fb: FormBuilder,
    private loadingListService: LoadingListService,
    private dialogRef: MatDialogRef<LoadingListInvalidateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LoadingListInvalidateDialogData
  ) {}

  ngOnInit(): void {
    const warehouses = this.data.warehouses ?? [];
    const preselected = warehouses.length === 1 ? warehouses[0].warehouseCode : null;

    this.form = this.fb.group({
      warehouseCode: [preselected, Validators.required],
      extra: [false],
      tourGroupIds: this.fb.array([this.createTourGroupControl()]),
      carrierCode: [null]
    });

    if (preselected) {
      this.loadTourGroups(preselected);
    }

    this.form.get('warehouseCode')!.valueChanges.subscribe((code) => {
      this.onWarehouseChanged(code);
    });

    this.form.get('extra')!.valueChanges.subscribe(() => {
      this.onExtraChanged();
    });
  }

  get tourGroupIdsArray(): FormArray<FormControl<number | null>> {
    return this.form.get('tourGroupIds') as FormArray<FormControl<number | null>>;
  }

  private createTourGroupControl(): FormControl<number | null> {
    return this.fb.control<number | null>(null, Validators.required);
  }

  private toNumber(v: any): number {
    if (v === null || v === undefined) return 0;
    if (typeof v === 'string') {
      const cleaned = v.replace(',', '.').trim();
      const n = Number(cleaned);
      return Number.isFinite(n) ? n : 0;
    }
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    if (typeof v === 'object' && 'value' in v) return this.toNumber(v.value);
    return 0;
  }

  private onWarehouseChanged(warehouseCode: string | null): void {
    const warehouse = this.data.warehouses.find(w => w.warehouseCode === warehouseCode);
    this.isExtraVisible = !!warehouse && warehouse.warehouseType !== 'TP';

    this.tourGroupIdsArray.clear();
    this.tourGroupIdsArray.push(this.createTourGroupControl());

    this.resetCarrierAndCarriersList();
    this.tourGroups = [];

    if (!warehouseCode) return;
    this.loadTourGroups(warehouseCode);
  }

  private onExtraChanged(): void {
    this.resetCarrierAndCarriersList();
    this.refreshCarriersFromSelectedGroups();
  }

  private resetCarrierAndCarriersList(): void {
    this.form.get('carrierCode')?.setValue(null);
    this.carriers = [];
  }

  loadTourGroups(warehouseCode: string): void {
    this.loadingTours = true;
    this.loadingListService.searchTourGroups({ warehouseCode })
      .pipe(finalize(() => (this.loadingTours = false)))
      .subscribe({
        next: (groups) => {
          this.tourGroups = groups ?? [];
          this.onTourGroupChanged();
        },
        error: console.error
      });
  }

  onTourGroupChanged(): void {
    this.resetCarrierAndCarriersList();
    this.refreshCarriersFromSelectedGroups();
  }

  private refreshCarriersFromSelectedGroups(): void {
    const extra = !!this.form.get('extra')?.value;
    const ids = this.getSelectedTourGroupIds();

    if (!extra && ids.length === 0) return;

    this.loadingCarriers = true;
    this.loadingListService.searchTourGroupCarriers({
      tourRoutingGroupId: ids,
      extra
    })
    .pipe(finalize(() => (this.loadingCarriers = false)))
    .subscribe({
      next: (res) => {
        this.carriers = res?.carriers ?? [];

        const defaultCarrier = this.carriers.find(c => c.defaultCarrier);
        if (defaultCarrier) {
          this.form.get('carrierCode')?.setValue(defaultCarrier.carrierCode);
        } else  {
          this.form.get('carrierCode')?.setValue(this.carriers[0].carrierCode);
        }
      },
      error: () => {
        this.carriers = [];
        this.form.get('carrierCode')?.setValue(null);
      }
    });
  }

  private getSelectedTourGroupIds(): number[] {
    const ids = this.tourGroupIdsArray.controls
      .map(c => c.value)
      .filter((v): v is number => v !== null);

    return Array.from(new Set(ids));
  }

  getAvailableTourGroupsForIndex(index: number): TourGroupsResponse[] {
    const selected = this.tourGroupIdsArray.controls
      .map((c, i) => (i === index ? null : c.value))
      .filter((v): v is number => v !== null);

    return this.tourGroups.filter(g => !selected.includes(g.tourRoutingGroupId));
  }

  addTourGroup(): void {
    if (!this.form.get('warehouseCode')?.value) return;
    this.tourGroupIdsArray.push(this.createTourGroupControl());
    this.resetCarrierAndCarriersList();
  }

  removeTourGroup(index: number): void {
    if (this.tourGroupIdsArray.length <= 1) return;
    this.tourGroupIdsArray.removeAt(index);
    this.resetCarrierAndCarriersList();
    this.refreshCarriersFromSelectedGroups();
  }

  hasAnyTourLeft(): boolean {
    const selected = this.getSelectedTourGroupIds();
    return this.tourGroups.some(t => !selected.includes(t.tourRoutingGroupId));
  }

  canAddMoreTours(index: number): boolean {
    const wh = this.form.get('warehouseCode')?.value;
    return !!wh && index === this.tourGroupIdsArray.length - 1 && this.hasAnyTourLeft();
  }

  private buildBaseRequest(): LoadingListManagementRequest | null {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return null;
    }

    const selectedTours = this.getSelectedTourGroupIds()
      .map(id => this.tourGroups.find(t => t.tourRoutingGroupId === id))
      .filter(Boolean)
      .map((tg: any) => ({
        tourRoutingGroupId: tg.tourRoutingGroupId,
        tourRoutingGroupName: tg.tourRoutingGroupName
      }));

    const carrierCode = this.form.value.carrierCode;
    const carrier = this.carriers.find(c => c.carrierCode === carrierCode);
    if (!carrier) {
      this.form.get('carrierCode')?.setErrors({ required: true });
      return null;
    }

    return {
      warehouseCode: this.form.value.warehouseCode,
      selectedTours,
      selectedCarrier: {
        carrierCode: carrier.carrierCode,
        carrierName: carrier.carrierName
      },
      extra: !!this.form.value.extra,
      loadingListPlateNo: '',
      loadingListType: 'DEPARTURE'
    };
  }

  goToItemsStep(): void {
    const req = this.buildBaseRequest();
    if (!req) return;

    this.loadingItems = true;
    this.loadingItemsSpinner = true;

    this.loadingListService.getValidLoadingListItemsToInvalidate(req)
      .pipe(finalize(() => {
        this.loadingItems = false;
        this.loadingItemsSpinner = false;
      }))
      .subscribe({
        next: (res) => {
          const items = res?.loadingListItems ?? [];

          this.validItems = items.map((it: any, index: number) => ({
            ...it,
            selected: false,
            weightKgNum: this.toNumber(it.weightKg),
            volumeM3Num: this.toNumber(it.volumeM3),
            originalIndex: index,
            parcelInactiveForLoadingLists: !!it.parcelInactiveForLoadingLists
          }));

          this.selectedTab = this.hasInvalidableItems ? 'invalidate' : 'validate';
          this.rebuildVisibleItems();
          this.step = 2;
        },
        error: (err) => {
          console.error('getValidLoadingListItems error', err);
          this.validItems = [];
          this.visibleItems = [];
        }
      });
  }

  get hasInvalidableItems(): boolean {
    return this.validItems.some(x => !x.parcelInactiveForLoadingLists);
  }

  get hasValidableItems(): boolean {
    return this.validItems.some(x => x.parcelInactiveForLoadingLists);
  }

  setTab(tab: LoadingListsAction): void {
    this.selectedTab = tab;
    this.rebuildVisibleItems();
  }

  private rebuildVisibleItems(): void {
    const barcode = (this.barcodeFilter || '').toLowerCase().trim();

    this.visibleItems = this.validItems
      .filter(it => this.selectedTab === 'invalidate'
        ? !it.parcelInactiveForLoadingLists
        : it.parcelInactiveForLoadingLists
      )
      .filter(it => !barcode || (it.barcode || it.parcelCode || '').toLowerCase().includes(barcode))
      .map(it => {
        const key = it.barcode || it.parcelCode;
        return {
        ...it,
        selected: this.selectedMap.get(key) || false
        };
      });

    this.sortVisibleItems();
    this.filteredItemsCount = this.visibleItems.length;
  }

  toggleItem(it: UiParcelItem): void {
    const key = it.barcode || it.parcelCode;
    const newValue = !this.selectedMap.get(key);
    this.selectedMap.set(key, newValue);
    it.selected = newValue;

    this.sortVisibleItems();
  }

    selectAll(): void {
        this.visibleItems.forEach(it => {
            const key = it.barcode || it.parcelCode;
            this.selectedMap.set(key, true);
            it.selected = true;
        });

        this.sortVisibleItems();
    }

    deselectAll(): void {
        this.visibleItems.forEach(it => {
            const key = it.barcode || it.parcelCode;
            this.selectedMap.set(key, false);
            it.selected = false;
        });

        this.sortVisibleItems();
    }


  onBarcodeFilterChange(value: string) {
  this.barcodeFilter = value;

  // debounce 350ms (puoi mettere 500 se lo vuoi più “calmo”)
  if (this.barcodeFilterTimer) {
    clearTimeout(this.barcodeFilterTimer);
  }

  this.barcodeFilterTimer = setTimeout(() => {
    this.applyBarcodeFilter();
  }, 350);
}


  clearBarcodeFilter(): void {
    this.barcodeFilter = '';

    if (this.barcodeFilterTimer) {
      clearTimeout(this.barcodeFilterTimer);
    }

    this.rebuildVisibleItems();
  }

  private applyBarcodeFilter(): void {
    this.rebuildVisibleItems();
  }

  private sortVisibleItems(): void {
    this.visibleItems = [...this.visibleItems].sort((a, b) => {

      // 1. NON selezionati sopra
      if (a.selected !== b.selected) {
        return a.selected ? 1 : -1;
      }

      // 2. Data affido DESC
      const dateA = a.parcelDate ? new Date(a.parcelDate).getTime() : 0;
      const dateB = b.parcelDate ? new Date(b.parcelDate).getTime() : 0;

      if (dateA !== dateB) {
        return dateB - dateA;
      }

      // 3. Urgenti prima
      const urgentA = !!a.urgencyCode;
      const urgentB = !!b.urgencyCode;

      if (urgentA !== urgentB) {
        return urgentA ? -1 : 1;
      }

      // 4. Ordine originale
      return a.originalIndex - b.originalIndex;
    });
  }

get selectedCount(): number {
  return this.validItems.filter(it => {
    const key = it.barcode || it.parcelCode;
    const isSelected = this.selectedMap.get(key) === true;

    const belongsToCurrentTab =
      this.selectedTab === 'invalidate'
        ? !it.parcelInactiveForLoadingLists
        : it.parcelInactiveForLoadingLists;

    return belongsToCurrentTab && isSelected;
  }).length;
}

  get totalCount(): number {
    return this.validItems
      .filter(it => this.selectedTab === 'invalidate'
        ? !it.parcelInactiveForLoadingLists
        : it.parcelInactiveForLoadingLists
      ).length;
  }

  get totalSelectedWeightKg(): number {
    return this.validItems
      .filter(it => this.selectedTab === 'invalidate'
        ? !it.parcelInactiveForLoadingLists
        : it.parcelInactiveForLoadingLists
      ).filter(i => i.selected).reduce((sum, i) => sum + i.weightKgNum, 0);
  }

  get totalSelectedVolumeM3(): number {
    return this.validItems
      .filter(it => this.selectedTab === 'invalidate'
        ? !it.parcelInactiveForLoadingLists
        : it.parcelInactiveForLoadingLists
      ).filter(i => i.selected).reduce((sum, i) => sum + i.volumeM3Num, 0);
  }

  get warehouseName(): string {
    const code = this.form.get('warehouseCode')?.value;
    return this.data.warehouses.find(w => w.warehouseCode === code)?.warehouseName ?? '';
  }

  get carrierName(): string {
    const code = this.form.get('carrierCode')?.value;
    return this.carriers.find(c => c.carrierCode === code)?.carrierName ?? '';
  }

  backToForm(): void {
    this.step = 1;
  }

  confirm(): void {
    const parcelCodes = this.validItems
      .filter(it =>
        this.selectedTab === 'invalidate'
          ? !it.parcelInactiveForLoadingLists
          : it.parcelInactiveForLoadingLists
      )
      .filter(it => {
        const key = it.barcode || it.parcelCode;
        return this.selectedMap.get(key) === true;
      })
      .map(it => it.parcelCode || it.barcode)
      .filter((code): code is string => !!code);

    if (!parcelCodes.length) return;

    this.dialogRef.close({
      parcelCodes,
      isToInvalid: this.selectedTab === 'invalidate',
      updatedCount: parcelCodes.length
    });
  }

  close(): void {
    this.dialogRef.close(null);
  }

  getItemBadgeLabel(it: UiParcelItem): string {
    if (this.selectedTab === 'invalidate') {
        return it.selected ? 'INVALIDO' : 'VALIDO';
    }

    return it.selected ? 'RIVALIDA' : 'INVALIDO';
    }

    getItemBadgeIcon(it: UiParcelItem): string {
    if (this.selectedTab === 'invalidate') {
        return it.selected ? 'fas fa-ban' : '';
    }

    return it.selected ? 'fas fa-undo' : 'fas fa-ban';
    }

    getItemBadgeClass(it: UiParcelItem): string {
    if (this.selectedTab === 'invalidate') {
        return it.selected ? 'status-badge pending' : 'status-badge neutral';
    }

    return it.selected ? 'status-badge validated' : 'status-badge neutral';
    }

    hasItemBadgeIcon(it: UiParcelItem): boolean {
    return !!this.getItemBadgeIcon(it);
    }


    
  get selectedTourGroupNames(): string {
    if (!this.form) return '';

    const ids = this.form.get('tourGroupIds')?.value as (number | null)[];

    return ids
      .filter((id): id is number => id !== null)
      .map(id => this.tourGroups.find(t => t.tourRoutingGroupId === id)?.tourRoutingGroupName)
      .filter((name): name is string => !!name)
      .join(', ');
  }

  toggleUrgencyFilter(): void {
    this.onlyUrgent = !this.onlyUrgent;
    this.applyFilters();
  }

  public applyFilters(): void {
    const barcode = (this.barcodeFilter || '').trim().toLowerCase();

    this.visibleItems = this.validItems
      .filter(it =>
        this.selectedTab === 'invalidate'
          ? !it.parcelInactiveForLoadingLists
          : it.parcelInactiveForLoadingLists
      )
      .filter(it =>
        !this.onlyUrgent || !!it.urgencyCode
      )
      .filter(it =>
        !barcode ||
        (it.barcode || it.parcelCode || '').toString().toLowerCase().includes(barcode)
      )
      .map(it => {
        const key = it.barcode || it.parcelCode;
        return {
          ...it,
          selected: this.selectedMap.get(key) === true
        };
      });

    this.sortVisibleItems();
    this.filteredItemsCount = this.visibleItems.length;
  }
}